/**
 * Capitalizing dictated text.
 *
 * The Web Speech API's German model reliably capitalizes nouns but just as
 * reliably leaves the first word of a short utterance lowercase — it never sees
 * the text already in the field. `unspokenPunctuation` fixes this at the
 * source, but only on desktop Chrome; on the Android tablets the field app runs
 * on, the fix has to happen here.
 *
 * The one rule these functions never break: they only ever make a letter
 * bigger. German capitalizes its nouns, so lowercasing on a guess would turn
 * correct text into wrong text.
 */

/**
 * Sentence-ending punctuation. A colon is deliberately absent — German writes
 * lowercase after it as often as not.
 */
const SENTENCE_END = /[.!?]/;

/**
 * True when text inserted after `before` starts a new sentence: `before` is
 * empty, ends a sentence, or ends a line.
 */
export function startsNewSentence(before: string): boolean {
    if (/\n\s*$/.test(before)) {
        return true;
    }
    const trimmed = before.trimEnd();
    if (trimmed.length === 0) {
        return true;
    }
    return SENTENCE_END.test(trimmed.slice(-1));
}

/** Upper-case the first letter, skipping any leading quotes or brackets. */
function capitalizeFirstLetter(text: string): string {
    return text.replace(/^([^\p{L}]*)(\p{L})/u, (_match, lead, letter) => {
        return `${lead}${letter.toUpperCase()}`;
    });
}

/**
 * Capitalize the sentence starts in `text`, given the text it will be inserted
 * after. Sentences already inside `text` are capitalized too — a dictated
 * "... fertig. danach folgt ..." gets its "danach" as well.
 */
export function capitalizeSentences(text: string, before: string): string {
    const withInner = text.replace(
        /([.!?]["'»)\]]?\s+)(\p{Ll})/gu,
        (_match, boundary, letter) => `${boundary}${letter.toUpperCase()}`
    );
    if (!startsNewSentence(before)) {
        return withInner;
    }
    return capitalizeFirstLetter(withInner);
}
